import { Card, Stack, SxProps, Tooltip, Typography, Box } from '@mui/material';
import React from 'react';
import Image from '#/components/shared/ui/Image';
import useLocales from '#/hooks/useLocales';
import Iconify from './ui/Iconify';

type Props = {
  title: string;
  icon?: string;
  description?: string;
  actions?: React.ReactNode;
  tooltipTitle?: string;
  showTooltip?: boolean;
  estimate?: string;
  noUploadYetRibbon?: boolean;
  sx?: SxProps;
};

export default function DashboardCard({
  title,
  icon,
  description,
  actions,
  tooltipTitle,
  showTooltip = false,
  estimate,
  sx,
  noUploadYetRibbon,
}: Props) {
  const { translate } = useLocales();

  // "Disabled" means showTooltip is true (i.e., nothing is shared with you yet)
  const isDisabled = showTooltip;

  return (
    <Tooltip title={tooltipTitle}>
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {isDisabled && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 0,
              zIndex: 2,
              background: '#f44336',
              color: '#fff',
              px: 2,
              py: 0.5,
              fontWeight: 700,
              fontSize: 12,
              borderRadius: '4px',
              boxShadow: 2,
              pointerEvents: 'none',
              minWidth: 160,
              textAlign: 'center',
            }}
          >
            {translate('global.dashCardInfos.guest.notYetShared') ||
              'Nothing is shared with you yet'}
          </Box>
        )}
        {noUploadYetRibbon && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 0,
              zIndex: 2,
              background: 'grey',
              color: '#fff',
              px: 2,
              py: 0.5,
              fontWeight: 700,
              fontSize: 12,
              borderRadius: '4px',
              boxShadow: 2,
              pointerEvents: 'none',
              minWidth: 160,
              textAlign: 'center',
            }}
          >
            {translate('global.dashCardInfos.guest.noDocumentUploadedRibbon')}
          </Box>
        )}
        <Card
          sx={{
            p: 2,
            flex: 1,
            ...sx,
          }}
        >
          {icon && (
            <Image
              src={icon}
              sx={{
                width: 60,
                height: 60,
                marginBottom: 2,
              }}
              alt={`${title} - ${title}`}
            />
          )}
          <Stack spacing={2}>
            <Typography
              variant="subtitle1"
              component="h6"
              sx={{ marginTop: 2 }}
            >
              {String(translate(title))}
            </Typography>
            <Typography fontWeight="normal" sx={{ height: '60px' }}>
              {translate(description as string)}
            </Typography>
            {estimate && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="ix:average" />
                <Typography fontWeight="normal">
                  {translate('global.dashCardInfos.timeToComplete', {
                    estimate: translate(estimate),
                  })}
                </Typography>
              </Stack>
            )}
          </Stack>
          {actions}
        </Card>
      </Box>
    </Tooltip>
  );
}
